import React from 'react';
import { Instagram, Smartphone } from './ui/Icons';
import { Link } from '../lib/router';
import { Button } from './ui/Button';
import { openTrackingSettings } from './ConsentBanner';

const TikTokIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} xmlns="http://www.w3.org/2000/svg">
    <path d="M19.5895 6.68627C18.667 6.47642 17.818 6.07542 17.1065 5.51478C16.3951 4.95413 15.8407 4.24949 15.4862 3.45667V2H12.0355V15.672C12.0355 16.486 11.7942 17.281 11.342 17.9579C10.8899 18.6348 10.2472 19.1627 9.49508 19.4742C8.74291 19.7857 7.91522 19.8672 7.11693 19.7084C6.31863 19.5496 5.58622 19.1576 5.01058 18.582C4.43494 18.0063 4.04297 17.2739 3.88417 16.4756C3.72537 15.6773 3.80687 14.8496 4.11837 14.0975C4.42988 13.3453 4.95772 12.7027 5.6346 12.2505C6.31149 11.7983 7.10647 11.557 7.92051 11.557C8.42398 11.557 8.91898 11.6461 9.38751 11.8211V8.29107C7.03521 8.29107 4.88122 9.68307 3.90022 11.8841C2.91922 14.0851 3.29122 16.6851 4.86422 18.5081C6.43722 20.3311 8.92221 21.0371 11.2982 20.3341C13.6742 19.6311 15.4862 17.6471 15.4862 15.1741V9.40007C16.7115 10.293 18.1717 10.7712 19.6905 10.7721V7.27207C19.6565 7.27233 19.6225 7.27233 19.5885 7.27207C19.5895 7.07632 19.5895 6.88082 19.5895 6.68627L19.5895 6.68627Z" />
  </svg>
);

export const Footer: React.FC = () => {
  return (
    <footer className="bg-black border-t border-white/10 py-16 relative z-10">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12 mb-12">
          
          {/* Brand Column */}
          <div className="lg:col-span-2">
            <Link to="/" className="inline-block">
              <h3 className="text-2xl font-bold mb-4 bg-gradient-to-tr from-[#eb46ff] to-[#5d3bee] bg-clip-text text-transparent">Night Navi</h3>
            </Link>
            <p className="text-gray-400 mb-6 max-w-sm">
              Die App des neuen Nachtlebens. Tickets, Rabatte und Freunde – alles in deiner Hand.
            </p>
            <div className="flex gap-4">
               <SocialIcon icon={Instagram} href="https://www.instagram.com/nightnavi.de/?hl=de" label="Instagram" gradient="from-yellow-500 via-red-500 to-purple-600" />
               <SocialIcon icon={TikTokIcon} href="https://www.tiktok.com/@nightnavi.de" label="TikTok" gradient="from-[#00f2ea] to-[#ff0050]" />
            </div>
          </div>

          {/* Links Column 1: Product */}
          <div>
            <h4 className="font-bold text-white mb-6">Produkt</h4>
            <ul className="space-y-4 text-sm text-gray-400">
              <li><Link to="/events" className="hover:text-purple-400 transition-colors">Events</Link></li>
              <li><Link to="/features" className="hover:text-purple-400 transition-colors">Features</Link></li>
              <li><Link to="/cities" className="hover:text-purple-400 transition-colors">Städte</Link></li>
              <li><Link to="/download" className="hover:text-purple-400 transition-colors">App laden</Link></li>
            </ul>
          </div>

          {/* Links Column 2: Business */}
          <div>
            <h4 className="font-bold text-white mb-6">Business</h4>
            <ul className="space-y-4 text-sm text-gray-400">
              <li><Link to="/partner" className="hover:text-purple-400 transition-colors">Partner werden</Link></li>
              <li><Link to="/organizer-info" className="hover:text-purple-400 transition-colors">Veranstalter</Link></li>
              <li><Link to="/bar-model" className="hover:text-purple-400 transition-colors">Bar Modell</Link></li>
              <li><Link to="/campaigns" className="hover:text-purple-400 transition-colors">Kampagnen</Link></li>
            </ul>
          </div>

           {/* Links Column 3: Company */}
          <div>
            <h4 className="font-bold text-white mb-6">Company</h4>
            <ul className="space-y-4 text-sm text-gray-400">
              <li><Link to="/vision" className="hover:text-purple-400 transition-colors">Vision</Link></li>
              <li><Link to="/faq" className="hover:text-purple-400 transition-colors">FAQ</Link></li>
              <li><Link to="/contact" className="hover:text-purple-400 transition-colors">Kontakt</Link></li>
            </ul>
          </div>
        </div>

        <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
           <div className="text-sm text-gray-600">
             © {new Date().getFullYear()} Night Navi UG (haftungsbeschränkt). Made in Leipzig.
           </div>
           <div className="flex flex-col md:flex-row gap-2 md:gap-6 text-sm text-gray-500 text-center md:text-left">
             <Link to="/rechtliches" className="hover:text-white transition-colors">Rechtliches</Link>
             <Link to="/impressum" className="hover:text-white transition-colors">Impressum</Link>
             <Link to="/widerruf" className="hover:text-white transition-colors">Widerruf</Link>
             <button type="button" onClick={openTrackingSettings} className="hover:text-white transition-colors">Tracking-Einstellungen</button>
           </div>
        </div>
      </div>
    </footer>
  );
};

const SocialIcon = ({ icon: Icon, href, label, gradient }: { icon: any, href: string, label: string, gradient: string }) => (
    <a href={href} target="_blank" rel="noopener noreferrer" className="group relative w-10 h-10 flex items-center justify-center" aria-label={label}>
        <div className={`absolute inset-0 bg-gradient-to-tr ${gradient} rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-lg`}></div>
        <div className="relative w-full h-full bg-white/5 border border-white/10 rounded-full flex items-center justify-center overflow-hidden group-hover:scale-105 transition-transform duration-300 group-hover:border-transparent">
            <div className={`absolute inset-0 bg-gradient-to-tr ${gradient} opacity-0 group-hover:opacity-20 transition-opacity duration-300`}></div>
            <Icon className="w-4 h-4 text-gray-400 group-hover:text-white relative z-10 transition-colors" />
        </div>
    </a>
);
